import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, UploadCloud, X, Loader2 } from "lucide-react";
import { API, formatApiError } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  REFERRAL_OPTIONS, SERVICES, GOALS, EXISTING_MATERIALS, WEBSITE_TYPES, PAGE_COUNTS,
  WEBSITE_FEATURES, CONTENT_NEEDED, YES_NO, YES_NO_NOTSURE, MEDIA_OPTIONS, BUDGETS,
  TIMELINES, CONTACT_PREFS, CONTACT_TIMES,
} from "./options";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+()\-.\s\d]{7,20}$/;
const URL_RE = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+([\/?#].*)?$/i;

const inputCls = "bg-[#0C0E14] border-white/10 focus:border-amber-400/50 h-11 text-slate-100";
const errCls = "text-xs text-amber-400/90 mt-1";

const initialAnswers = {
  contact: { firstName: "", lastName: "", company: "", email: "", phone: "", website: "", referralSource: "" },
  services: [],
  project: { description: "", goals: [], existingMaterials: "" },
  website: { hasWebsite: "", currentWebsite: "", websiteType: "", pageCount: "", features: [], contentNeeded: "" },
  brand: { logo: "", brandColors: "", media: "", brandGuidelines: "" },
  budget: { estimatedBudget: "", timeline: "", hasDeadline: "", deadline: "" },
  final: { additionalDetails: "", contactPreference: [], preferredContactTime: "" },
};

function Field({ label, required, error, children, htmlFor }) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={htmlFor} className="text-slate-300 text-sm">
        {label} {required && <span className="text-amber-400">*</span>}
      </Label>
      {children}
      {error && <p role="alert" className={errCls} data-testid={`error-${htmlFor}`}>{error}</p>}
    </div>
  );
}

function Question({ title, children }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-semibold text-slate-200 tracking-wide">{title}</p>
      {children}
    </div>
  );
}

function PillGroup({ options, value, onSelect, multi = false, testid }) {
  const toggle = (opt) => {
    if (multi) {
      onSelect(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
    } else {
      onSelect(value === opt ? "" : opt);
    }
  };
  return (
    <div className="flex flex-wrap gap-2.5" data-testid={testid}>
      {options.map((opt) => {
        const selected = multi ? value.includes(opt) : value === opt;
        return (
          <button key={opt} type="button" onClick={() => toggle(opt)} aria-pressed={selected}
            data-testid={`${testid}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`notched-sm px-4 py-2.5 text-sm border transition-all duration-300 ${
              selected
                ? "border-amber-400/60 bg-amber-400/10 text-amber-200 shadow-[0_0_16px_rgba(212,175,55,0.15)]"
                : "border-white/12 bg-[#0C0E14] text-slate-300 hover:border-amber-400/40 hover:text-amber-300"
            }`}>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export default function IntakeForm() {
  const [answers, setAnswers] = useState(initialAnswers);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [active, setActive] = useState(0);
  const [errors, setErrors] = useState({});
  const [mode, setMode] = useState("form"); // form | review | success
  const [submitting, setSubmitting] = useState(false);
  const cardRefs = useRef({});

  const needsWebsite = answers.services.includes("website") || answers.services.includes("multiple");

  const steps = useMemo(() => {
    const all = [
      { id: "contact", title: "Contact Information", heading: "Let's Start With You", desc: "Tell us a little about yourself and your business." },
      { id: "services", title: "Services You Need", heading: "What Can We Create For You?", desc: "Select the service or services you're interested in." },
      { id: "project", title: "Your Project", heading: "Tell Us About Your Project", desc: "The more you tell us, the better we can understand your vision." },
      { id: "website", title: "Website Details", heading: "Let's Talk Website", desc: "Tell us what you're looking for in your website.", conditional: true },
      { id: "brand", title: "Brand & Content", heading: "Your Brand", desc: "Help us understand what you already have and what you may need." },
      { id: "budget", title: "Budget & Timeline", heading: "Let's Talk Budget & Timeline", desc: "This helps us recommend the right approach for your project." },
      { id: "final", title: "Final Details", heading: "Almost There", desc: "Just a few final details before you send your project inquiry." },
    ];
    return all.filter((s) => !s.conditional || needsWebsite);
  }, [needsWebsite]);

  const set = (section, key) => (e) => {
    const v = e?.target ? e.target.value : e;
    setAnswers((a) => ({ ...a, [section]: { ...a[section], [key]: v } }));
    setErrors((er) => ({ ...er, [`${section}.${key}`]: null }));
  };

  const validateStep = (stepId) => {
    const er = {};
    if (stepId === "contact") {
      const c = answers.contact;
      if (!c.firstName.trim()) er["contact.firstName"] = "Please enter your first name.";
      if (!c.lastName.trim()) er["contact.lastName"] = "Please enter your last name.";
      if (!c.company.trim()) er["contact.company"] = "Please enter your business name.";
      if (!EMAIL_RE.test(c.email)) er["contact.email"] = "Please enter a valid email address.";
      if (!PHONE_RE.test(c.phone.trim())) er["contact.phone"] = "Please enter a valid phone number.";
      if (c.website.trim() && !URL_RE.test(c.website.trim())) er["contact.website"] = "Please enter a valid URL.";
    }
    if (stepId === "services" && answers.services.length === 0) {
      er["services"] = "Please select at least one service.";
    }
    return er;
  };

  const goNext = () => {
    const step = steps[active];
    const er = validateStep(step.id);
    setErrors(er);
    if (Object.values(er).some(Boolean)) return;
    if (active === steps.length - 1) {
      setMode("review");
      window.scrollTo({ top: document.getElementById("contact").offsetTop - 80, behavior: "smooth" });
      return;
    }
    const next = active + 1;
    setActive(next);
    setTimeout(() => cardRefs.current[steps[next].id]?.scrollIntoView({ behavior: "smooth", block: "center" }), 250);
  };

  const goBack = () => setActive((i) => Math.max(0, i - 1));

  const editStep = (i) => {
    setMode("form");
    setActive(i);
    setTimeout(() => cardRefs.current[steps[i].id]?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
  };

  const uploadFiles = async (fileList) => {
    const list = Array.from(fileList).slice(0, 8 - files.length);
    if (!list.length) return;
    setUploading(true);
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await axios.post(`${API}/uploads`, fd);
        setFiles((prev) => [...prev, data]);
      } catch (e) {
        toast.error(`${f.name}: ${formatApiError(e)}`);
      }
    }
    setUploading(false);
  };

  const submit = async () => {
    setSubmitting(true);
    const c = answers.contact;
    const primary = answers.services.includes("website") ? "website"
      : answers.services[0] === "multiple" || answers.services[0] === "not-sure" ? "other"
      : answers.services[0] || "other";
    try {
      await axios.post(`${API}/inquiries`, {
        name: `${c.firstName} ${c.lastName}`.trim(),
        email: c.email,
        phone: c.phone,
        service: primary,
        budget: answers.budget.estimatedBudget,
        details: answers.project.description,
        source: c.referralSource,
        intake: answers,
        files,
      });
      setMode("success");
      window.scrollTo({ top: document.getElementById("contact").offsetTop - 40, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const c = answers.contact;

  const renderFields = (step) => {
    switch (step.id) {
      case "contact":
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="First Name" required error={errors["contact.firstName"]} htmlFor="firstName">
              <Input id="firstName" data-testid="intake-first-name" value={c.firstName} onChange={set("contact", "firstName")} aria-invalid={!!errors["contact.firstName"]} className={inputCls} placeholder="Jane" />
            </Field>
            <Field label="Last Name" required error={errors["contact.lastName"]} htmlFor="lastName">
              <Input id="lastName" data-testid="intake-last-name" value={c.lastName} onChange={set("contact", "lastName")} aria-invalid={!!errors["contact.lastName"]} className={inputCls} placeholder="Doe" />
            </Field>
            <Field label="Business / Company Name" required error={errors["contact.company"]} htmlFor="company">
              <Input id="company" data-testid="intake-company" value={c.company} onChange={set("contact", "company")} aria-invalid={!!errors["contact.company"]} className={inputCls} placeholder="Acme Co." />
            </Field>
            <Field label="Email Address" required error={errors["contact.email"]} htmlFor="email">
              <Input id="email" type="email" data-testid="intake-email" value={c.email} onChange={set("contact", "email")} aria-invalid={!!errors["contact.email"]} className={inputCls} placeholder="you@company.com" />
            </Field>
            <Field label="Phone Number" required error={errors["contact.phone"]} htmlFor="phone">
              <Input id="phone" type="tel" data-testid="intake-phone" value={c.phone} onChange={set("contact", "phone")} aria-invalid={!!errors["contact.phone"]} className={inputCls} placeholder="(360) 555-0100" />
            </Field>
            <Field label="Website URL" htmlFor="website" error={errors["contact.website"]}>
              <Input id="website" data-testid="intake-website-url" value={c.website} onChange={set("contact", "website")} className={inputCls} placeholder="https://yoursite.com (optional)" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="How did you hear about M&M Productions?" htmlFor="referral">
                <Select value={c.referralSource} onValueChange={set("contact", "referralSource")}>
                  <SelectTrigger data-testid="intake-referral" className={`${inputCls} w-full`}>
                    <SelectValue placeholder="Select one (optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12141C] border-white/10">
                    {REFERRAL_OPTIONS.map((o) => (
                      <SelectItem key={o} value={o} data-testid={`referral-${o.toLowerCase().replace(/\s+/g, "-")}`}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </div>
        );
      case "services":
        return (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="service-select-grid">
              {SERVICES.map((s) => {
                const selected = answers.services.includes(s.id);
                return (
                  <button key={s.id} type="button" aria-pressed={selected}
                    data-testid={`service-option-${s.id}`}
                    onClick={() => setAnswers((a) => ({
                      ...a,
                      services: selected ? a.services.filter((x) => x !== s.id) : [...a.services, s.id],
                    }))}
                    className={`relative text-left notched p-5 border transition-all duration-300 ${
                      selected
                        ? "border-amber-400/70 bg-amber-400/[0.07] shadow-[0_0_25px_rgba(212,175,55,0.15)]"
                        : s.featured
                          ? "border-amber-400/35 bg-[#12141C] hover:border-amber-400/60"
                          : "border-white/10 bg-[#0C0E14] hover:border-amber-400/40"
                    }`}>
                    {s.featured && (
                      <span className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-[0.18em] text-black bg-amber-400 px-2 py-0.5">Most Popular</span>
                    )}
                    {selected && (
                      <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute bottom-3 right-3 h-5 w-5 rounded-full bg-amber-400 flex items-center justify-center">
                        <Check className="h-3 w-3 text-black" strokeWidth={3.5} />
                      </motion.span>
                    )}
                    <p className={`font-heading text-sm sm:text-base font-semibold ${selected ? "text-amber-200" : "text-slate-100"}`}>{s.title}</p>
                    <p className="mt-1.5 text-xs text-slate-400 leading-relaxed pr-6">{s.description}</p>
                  </button>
                );
              })}
            </div>
            {errors["services"] && <p role="alert" className={`${errCls} mt-3`} data-testid="error-services">{errors["services"]}</p>}
          </div>
        );
      case "project":
        return (
          <div className="flex flex-col gap-6">
            <Field label="What are you looking to accomplish?" htmlFor="description">
              <Textarea id="description" data-testid="intake-description" rows={5} value={answers.project.description}
                onChange={set("project", "description")}
                placeholder="Tell us about your business, project, idea, or what you'd like M&M Productions to create..."
                className="bg-[#0C0E14] border-white/10 focus:border-amber-400/50 resize-none text-slate-100" />
            </Field>
            <Question title="What is the main goal of this project?">
              <PillGroup options={GOALS} value={answers.project.goals} multi testid="goal"
                onSelect={(v) => set("project", "goals")(v)} />
            </Question>
            <Question title="Do you already have an existing project or materials?">
              <PillGroup options={EXISTING_MATERIALS} value={answers.project.existingMaterials} testid="materials"
                onSelect={(v) => set("project", "existingMaterials")(v)} />
            </Question>
          </div>
        );
      case "website": {
        const w = answers.website;
        return (
          <div className="flex flex-col gap-6">
            <Question title="Do you currently have a website?">
              <PillGroup options={YES_NO} value={w.hasWebsite} testid="has-website" onSelect={(v) => set("website", "hasWebsite")(v)} />
            </Question>
            {w.hasWebsite === "Yes" && (
              <Field label="Current Website URL" htmlFor="currentWebsite">
                <Input id="currentWebsite" data-testid="intake-current-website" value={w.currentWebsite}
                  onChange={set("website", "currentWebsite")} className={inputCls} placeholder="https://yourcurrentsite.com" />
              </Field>
            )}
            <Question title="What do you need?">
              <PillGroup options={WEBSITE_TYPES} value={w.websiteType} testid="website-type" onSelect={(v) => set("website", "websiteType")(v)} />
            </Question>
            <Question title="How many pages do you approximately need?">
              <PillGroup options={PAGE_COUNTS} value={w.pageCount} testid="page-count" onSelect={(v) => set("website", "pageCount")(v)} />
            </Question>
            <Question title="What features do you need?">
              <PillGroup options={WEBSITE_FEATURES} value={w.features} multi testid="feature"
                onSelect={(v) => set("website", "features")(v)} />
            </Question>
            <Question title="Do you need us to provide the website content?">
              <PillGroup options={CONTENT_NEEDED} value={w.contentNeeded} testid="content-needed" onSelect={(v) => set("website", "contentNeeded")(v)} />
            </Question>
          </div>
        );
      }
      case "brand": {
        const b = answers.brand;
        return (
          <div className="flex flex-col gap-6">
            <Question title="Do you already have a logo?">
              <PillGroup options={YES_NO} value={b.logo} testid="has-logo" onSelect={(v) => set("brand", "logo")(v)} />
            </Question>
            <Question title="Do you already have established brand colors?">
              <PillGroup options={YES_NO_NOTSURE} value={b.brandColors} testid="brand-colors" onSelect={(v) => set("brand", "brandColors")(v)} />
            </Question>
            <Question title="Do you already have photos/videos for the project?">
              <PillGroup options={MEDIA_OPTIONS} value={b.media} testid="has-media" onSelect={(v) => set("brand", "media")(v)} />
            </Question>
            <Question title="Do you have existing brand guidelines?">
              <PillGroup options={YES_NO_NOTSURE} value={b.brandGuidelines} testid="brand-guidelines" onSelect={(v) => set("brand", "brandGuidelines")(v)} />
            </Question>
            <div className="flex flex-col gap-3">
              <p className="text-sm font-semibold text-slate-200 tracking-wide">Upload Project Files <span className="text-slate-500 font-normal">(optional)</span></p>
              <label
                data-testid="file-drop-zone"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); uploadFiles(e.dataTransfer.files); }}
                className="notched cursor-pointer border border-dashed border-amber-400/40 bg-[#0C0E14] hover:bg-amber-400/5 transition-colors duration-300 px-6 py-8 flex flex-col items-center gap-2 text-center"
              >
                <UploadCloud className="h-7 w-7 text-amber-400/80" />
                <span className="text-sm text-slate-300">{uploading ? "Uploading…" : "Drag & drop files here, or tap to browse"}</span>
                <span className="text-xs text-slate-500">Logos, images, videos, brand guidelines, documents — max 25MB each</span>
                <input type="file" multiple className="hidden" data-testid="file-input"
                  accept="image/*,video/mp4,video/quicktime,.pdf,.doc,.docx,.txt,.zip"
                  onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
              </label>
              {files.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="uploaded-files">
                  {files.map((f) => (
                    <span key={f.path} className="inline-flex items-center gap-2 notched-sm border border-amber-400/30 bg-amber-400/5 px-3 py-1.5 text-xs text-slate-300">
                      {f.name}
                      <button type="button" aria-label={`Remove ${f.name}`} data-testid={`remove-file-${f.name}`}
                        onClick={() => setFiles((prev) => prev.filter((x) => x.path !== f.path))}
                        className="text-slate-500 hover:text-red-300 transition-colors">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      }
      case "budget": {
        const b = answers.budget;
        return (
          <div className="flex flex-col gap-6">
            <Question title="What's your estimated budget?">
              <PillGroup options={BUDGETS} value={b.estimatedBudget} testid="budget" onSelect={(v) => set("budget", "estimatedBudget")(v)} />
            </Question>
            <Question title="When would you like to get started?">
              <PillGroup options={TIMELINES} value={b.timeline} testid="timeline" onSelect={(v) => set("budget", "timeline")(v)} />
            </Question>
            <Question title="Is there a specific deadline?">
              <PillGroup options={YES_NO} value={b.hasDeadline} testid="has-deadline" onSelect={(v) => set("budget", "hasDeadline")(v)} />
            </Question>
            {b.hasDeadline === "Yes" && (
              <Field label="Deadline" htmlFor="deadline">
                <Input id="deadline" type="date" data-testid="intake-deadline" value={b.deadline}
                  onChange={set("budget", "deadline")} className={`${inputCls} [color-scheme:dark]`} />
              </Field>
            )}
          </div>
        );
      }
      case "final":
        return (
          <div className="flex flex-col gap-6">
            <Field label="Anything else you'd like us to know?" htmlFor="additional">
              <Textarea id="additional" data-testid="intake-additional" rows={5} value={answers.final.additionalDetails}
                onChange={set("final", "additionalDetails")}
                placeholder="Additional details, ideas, questions, references, or anything else you'd like us to know..."
                className="bg-[#0C0E14] border-white/10 focus:border-amber-400/50 resize-none text-slate-100" />
            </Field>
            <Question title="How would you like us to contact you?">
              <PillGroup options={CONTACT_PREFS} value={answers.final.contactPreference} multi testid="contact-pref"
                onSelect={(v) => set("final", "contactPreference")(v)} />
            </Question>
            <Question title="Best time to reach you?">
              <PillGroup options={CONTACT_TIMES} value={answers.final.preferredContactTime} testid="contact-time"
                onSelect={(v) => set("final", "preferredContactTime")(v)} />
            </Question>
          </div>
        );
      default:
        return null;
    }
  };

  const reviewSections = [
    { key: "contact", title: "Contact Information", step: 0, rows: [
      ["Name", `${c.firstName} ${c.lastName}`], ["Business", c.company], ["Email", c.email], ["Phone", c.phone],
      ["Website", c.website], ["Heard via", c.referralSource],
    ]},
    { key: "services", title: "Services", step: 1, rows: [["Selected", answers.services.map((s) => SERVICES.find((x) => x.id === s)?.title).filter(Boolean).join(", ")]] },
    { key: "project", title: "Project", step: 2, rows: [
      ["Vision", answers.project.description], ["Goals", answers.project.goals.join(", ")], ["Existing materials", answers.project.existingMaterials],
    ]},
    ...(needsWebsite ? [{ key: "website", title: "Website", step: 3, rows: [
      ["Has website", answers.website.hasWebsite], ["Current URL", answers.website.currentWebsite],
      ["Type", answers.website.websiteType], ["Pages", answers.website.pageCount],
      ["Features", answers.website.features.join(", ")], ["Content needed", answers.website.contentNeeded],
    ]}] : []),
    { key: "brand", title: "Brand & Content", step: needsWebsite ? 4 : 3, rows: [
      ["Logo", answers.brand.logo], ["Brand colors", answers.brand.brandColors], ["Photos/videos", answers.brand.media],
      ["Guidelines", answers.brand.brandGuidelines], ["Files", files.map((f) => f.name).join(", ")],
    ]},
    { key: "budget", title: "Budget & Timeline", step: needsWebsite ? 5 : 4, rows: [
      ["Budget", answers.budget.estimatedBudget], ["Start", answers.budget.timeline],
      ["Deadline", answers.budget.hasDeadline === "Yes" ? answers.budget.deadline : answers.budget.hasDeadline],
    ]},
    { key: "final", title: "Contact Preference", step: needsWebsite ? 6 : 5, rows: [
      ["Contact via", answers.final.contactPreference.join(", ")], ["Best time", answers.final.preferredContactTime],
      ["Notes", answers.final.additionalDetails],
    ]},
  ];

  return (
    <section id="contact" data-testid="contact-section" className="px-4 sm:px-6 lg:px-12 py-16 md:py-24 bg-black">
      <div className="max-w-3xl mx-auto">
        <div className="mb-12 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-amber-400/90 font-medium mb-4">M&M Productions</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" data-testid="intake-headline">
            Let's Build <span className="text-[#D4AF37]">Something.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">
            Tell us about your project and let's turn your idea into reality.
          </p>
          {mode === "form" && (
            <div className="mt-8 max-w-md mx-auto" data-testid="intake-progress">
              <p className="text-[11px] tracking-[0.25em] uppercase text-slate-500 mb-2">
                Step {active + 1} of {steps.length}
              </p>
              <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                <motion.div className="h-full bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37]"
                  animate={{ width: `${((active + 1) / steps.length) * 100}%` }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} />
              </div>
            </div>
          )}
        </div>

        {mode === "success" ? (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
            data-testid="intake-success"
            className="notched border border-amber-400/30 bg-[#12141C] px-6 sm:px-12 py-14 text-center">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 14 }}
              className="mx-auto h-16 w-16 rounded-full border border-amber-400/50 bg-amber-400/10 flex items-center justify-center shadow-[0_0_40px_rgba(212,175,55,0.25)]">
              <Check className="h-8 w-8 text-amber-400" strokeWidth={3} />
            </motion.div>
            <h3 className="font-heading text-2xl sm:text-3xl font-bold mt-8">Project Received.</h3>
            <p className="mt-4 text-sm sm:text-base text-slate-300 max-w-md mx-auto leading-relaxed">
              Thank you for reaching out to M&M Productions. We've received your project inquiry and will review the details you've provided.
            </p>
            <p className="mt-2 text-xs text-slate-500">We'll be in touch with you soon regarding the next steps.</p>
            <a href="#top" data-testid="success-back-btn"
              className="btn-fill notched-sm mt-8 inline-flex items-center gap-2 border border-amber-400/40 bg-transparent px-7 py-3 text-sm font-semibold text-amber-300 hover:text-black transition-[color,box-shadow] duration-300">
              Back to M&M Productions
            </a>
          </motion.div>
        ) : mode === "review" ? (
          <div data-testid="intake-review" className="notched border border-amber-400/30 bg-[#12141C] p-6 sm:p-10">
            <h3 className="font-heading text-xl sm:text-2xl font-bold mb-8">Review Your Project</h3>
            <div className="flex flex-col gap-6">
              {reviewSections.map((sec) => (
                <div key={sec.key} className="border-b border-white/5 pb-5 last:border-0" data-testid={`review-${sec.key}`}>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[11px] uppercase tracking-[0.25em] text-amber-400/80">{sec.title}</p>
                    <button type="button" onClick={() => editStep(sec.step)} data-testid={`review-edit-${sec.key}`}
                      className="text-xs text-slate-400 hover:text-amber-300 transition-colors">
                      Edit →
                    </button>
                  </div>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                    {sec.rows.filter(([, v]) => v && String(v).trim()).map(([k, v]) => (
                      <div key={k} className="flex flex-col">
                        <dt className="text-[11px] text-slate-500">{k}</dt>
                        <dd className="text-sm text-slate-200 break-words">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-col sm:flex-row items-center gap-4">
              <button type="button" onClick={() => setMode("form")} data-testid="review-back-btn"
                className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-300 transition-colors">
                <span className="font-serif font-bold">❮</span> Back
              </button>
              <div className="flex-1" />
              <div className="flex flex-col items-center sm:items-end gap-2 w-full sm:w-auto">
                <p className="text-xs text-slate-500 tracking-[0.15em] uppercase">Everything look good?</p>
                <button type="button" onClick={submit} disabled={submitting} data-testid="submit-inquiry-btn"
                  className="btn-fill notched-sm inline-flex items-center gap-2 border border-amber-400/50 bg-transparent px-7 py-3 text-sm font-bold text-amber-300 hover:text-black hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-[color,box-shadow] duration-300 disabled:opacity-60">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Submit Project Inquiry <span className="font-serif font-bold">❯</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4" data-testid="intake-form">
            {steps.map((step, i) => {
              const isActive = i === active;
              const isDone = i < active;
              return (
                <div key={step.id} ref={(el) => (cardRefs.current[step.id] = el)}
                  data-testid={`step-card-${step.id}`}
                  className={`notched transition-all duration-500 ${
                    isActive
                      ? "border border-amber-400/40 bg-[#12141C] shadow-[0_0_35px_rgba(212,175,55,0.1)]"
                      : isDone
                        ? "border border-white/10 bg-[#0E1016]"
                        : "border border-white/5 bg-[#0B0C11] opacity-60"
                  }`}>
                  <button type="button" disabled={!isDone && !isActive} onClick={() => isDone && editStep(i)}
                    data-testid={`step-header-${step.id}`}
                    className="relative w-full flex items-center justify-center gap-3 px-5 sm:px-7 py-4 text-center">
                    {isDone ? (
                      <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }}
                        className="absolute left-5 sm:left-7 h-6 w-6 rounded-full bg-amber-400 flex items-center justify-center">
                        <Check className="h-3.5 w-3.5 text-black" strokeWidth={3.5} />
                      </motion.span>
                    ) : (
                      <span className={`absolute left-5 sm:left-7 text-xs font-bold tracking-widest ${isActive ? "text-amber-400" : "text-slate-600"}`}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    )}
                    <span className={`text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold ${isActive ? "text-slate-100" : isDone ? "text-slate-300" : "text-slate-600"}`}>
                      {step.title}
                    </span>
                    {isDone && <span className="absolute right-5 sm:right-7 text-xs text-slate-500 hover:text-amber-300 transition-colors">Edit →</span>}
                  </button>
                  <AnimatePresence initial={false}>
                    {isActive && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden">
                        <div className="px-5 sm:px-7 pb-7 pt-1">
                          <div className="text-center">
                            <h3 className="font-heading text-lg sm:text-xl font-semibold mb-1">{step.heading}</h3>
                            <p className="text-xs sm:text-sm text-slate-500 mb-6">{step.desc}</p>
                          </div>
                          {renderFields(step)}
                          <div className="mt-8 relative flex items-center justify-center">
                            {i > 0 && (
                              <button type="button" onClick={goBack} data-testid="step-back-btn"
                                className="absolute left-0 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-300 transition-colors">
                                <span className="font-serif font-bold">❮</span> Back
                              </button>
                            )}
                            <button type="button" onClick={goNext} data-testid="step-continue-btn"
                              className="btn-fill notched-sm inline-flex items-center gap-2 border border-amber-400/50 bg-transparent px-7 py-3 text-sm font-bold text-amber-300 hover:text-black hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-[color,box-shadow] duration-300">
                              {i === steps.length - 1 ? "Review My Project" : "Continue"} <span className="font-serif font-bold">❯</span>
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
